import camelCaseKeys from "camelcase-keys";
import Cookies from "js-cookie";
import moment from "moment";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { API_HOST } from "../../config";
import { LoadingContainer } from "../../containers/LoadingContainer";
import { TalkListContainer } from "../../containers/TalkListContainer";
import useInterbal from "../../customHook/UseInterbal";
import history from "../../history";
import { IMessage, IUser } from "../../interfaces";
import axios from "../../modules/axiosInstance";
import LoadingIcon from "../atoms/LoadingIcon";
import MessageDialog from "../organisms/MessageDialog";
import MessageFooter from "../organisms/MessageFooter";
import MessageHeader from "../organisms/MessageHeader";
import MessageList from "../organisms/MessageList";

interface IProps {
  location: {
    pathname: string;
  };
}

const Message: React.FC<IProps> = ({ location, ...props }) => {
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [user, setUser] = useState<IUser | null>(null);
  const [sending, setSending] = useState<boolean>(false);
  const [imageModalOpen, setImageModalOpen] = useState<boolean>(false);
  const [value, setValue] = useState<string>("");
  const [image, setImage] = useState<File | null>(null);
  const [error, setError] = useState<string>("");
  const loading = LoadingContainer.useContainer();
  const fetchTalkList = TalkListContainer.useContainer().fetchTalks;
  const messagesEndRef = React.createRef<HTMLDivElement>();

  const scrollToBottom = (): void => {
    if (messagesEndRef.current !== null) {
      messagesEndRef.current.scrollIntoView({ behavior: "auto" });
    }
  };

  const fetchMessages = () => {
    const userId = location.pathname.split("/")[2];
    const url = `${API_HOST}/api/users/${userId}/messages`;
    loading.start();
    axios
      .get(url)
      .then(results => {
        const data: any = camelCaseKeys(results.data, { deep: true });
        const messages: IMessage[] = data.messages;
        const user: IUser = data;
        setMessages([...messages]);
        setUser(user);
        loading.done();
      })
      .catch(e => {
        loading.done();
        console.error(e);
        history.push("/login");
      });
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessagesWithlastMessageId = (lastMessageId: number): void => {
    const userId = location.pathname.split("/")[2];
    const url = `${API_HOST}/api/users/${userId}/messages`;
    axios
      .get(url, {
        params: {
          last_message_id: lastMessageId
        }
      })
      .then(results => {
        const data: any = camelCaseKeys(results.data, { deep: true });
        const newMessages: IMessage[] = data;
        setMessages([...messages.concat(newMessages)]);
      })
      .catch(e => {
        console.error(e);
      });
  };

  useInterbal(() => {
    if (messages.length > 0) {
      const lastMessageId = messages.slice(-1)[0].id;
      fetchMessagesWithlastMessageId(lastMessageId);
    } else {
      fetchMessages();
    }
  }, 5000);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // TODO: anyではなく型付けをしたい
  const handleImageChange = (e: any): void => {
    const imageFile = e.target.files[0];
    try {
      validateImage(imageFile);
      setImage(imageFile);
    } catch (error) {
      setImage(null);
      console.error(error);
    }
  };

  // TODO: anyではなく型付けをしたい
  const validateImage = (imageFile: any): void => {
    if (imageFile === null) {
      const message = "ファイルが選択されていません。";
      setError(message);
      throw new Error(message);
    } else if (!imageFile.name.match(/\.(jpg|jpeg|png|svg|gif)$/i)) {
      const message = "ファイル形式が適切ではありません。";
      setError(message);
      throw new Error(message);
    } else if (imageFile.size > 5000000) {
      const message = "ファイル容量が大きすぎます。";
      setError(message);
      throw new Error(message);
    } else {
      setError("");
    }
  };

  const handleTextSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (sending || !user) {
      return;
    }
    if (value.length === 0) {
      return;
    }
    setSending(true);
    const url = `${API_HOST}/api/users/${user.id}/messages`;
    const admin_id = Cookies.get("admin_id");
    const params = new FormData();
    params.append("value", value);
    params.append("admin_id", admin_id as string);
    axios
      .post(url, params)
      .then(result => {
        const data: any = camelCaseKeys(result.data, { deep: true });
        setMessages([...messages, data.message]);
        setError("");
        setValue("");
        setSending(false);
        scrollToBottom();
      })
      .catch(err => console.error(err));
  };

  const handleImageSubmit = async () => {
    if (sending || !user) {
      return;
    }
    if (image === null) {
      return;
    }
    setSending(true);
    const url = `${API_HOST}/api/users/${user.id}/messages`;
    const admin_id = Cookies.get("admin_id");
    const params = new FormData();
    params.append("image", image);
    params.append("admin_id", admin_id as string);
    await axios
      .post(url, params, {
        headers: {
          "content-type": "multipart/form-data"
        }
      })
      .then(result => {
        const data: any = camelCaseKeys(result.data, { deep: true });
        setMessages([...messages, data.message]);
        setSending(false);
        handleCloseImageModal();
        scrollToBottom();
      })
      .catch(e => {
        console.error(e);
      });
  };

  const handleOpenImageModal = (): void => {
    setImageModalOpen(true);
  };

  const handleCloseImageModal = (): void => {
    setImageModalOpen(false);
  };

  const changeCorrespondenceStatus = (): void => {
    if (user === null) {
      return;
    }
    const url = `${API_HOST}/api/users/${user.id}`;
    const params = new FormData();
    params.append("correspondence_status", user.correspondenceStatus);
    axios
      .put(url, params)
      .then(result => {
        const data: any = camelCaseKeys(result.data, { deep: true });
        const userData: IUser = data.user;
        setUser(userData);
        fetchTalkList();
      })
      .catch(err => console.error(err));
  };

  const handleTextChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ): void => {
    setValue(e.target.value);
  };

  const dateText = (message: IMessage, index: number): string => {
    const prevMessage = index !== 0 ? messages[index - 1] : null;
    const prevTime = prevMessage ? moment(prevMessage.timestamp) : null;
    const time = moment(message.timestamp);
    const displayDate =
      !prevTime || prevTime.format("YYYYMMDD") !== time.format("YYYYMMDD");
    const now = moment();
    if (displayDate) {
      if (now.format("YYYYMMDD") === time.format("YYYYMMDD")) {
        return "今日";
      }
      if (
        now.subtract(1, "days").format("YYYYMMDD") === time.format("YYYYMMDD")
      ) {
        return "昨日";
      }
      return time.format("M月D日");
    }
    return "";
  };

  return (
    <TalkPage>
      <MessageHeader
        user={user}
        changeCorrespondenceStatus={changeCorrespondenceStatus}
      />

      {!loading.isloading ? (
        <MessageList messages={messages} dateText={dateText}>
          <MessageFooter
            handleOpenImageModal={handleOpenImageModal}
            handleTextSubmit={handleTextSubmit}
            value={value}
            handleTextChange={handleTextChange}
            scrollToBottom={scrollToBottom}
            sending={sending}
          />
        </MessageList>
      ) : (
        <LoadingIcon />
      )}

      <MessageDialog
        handleCloseImageModal={handleCloseImageModal}
        imageModalOpen={imageModalOpen}
        handleImageChange={handleImageChange}
        error={error}
        handleImageSubmit={handleImageSubmit}
      />
      <div ref={messagesEndRef} />
    </TalkPage>
  );
};

const TalkPage = styled.div`
  padding-top: 52px;
`;

export default Message;
